/*
 * API khai báo y tế (/api/v2/declarations) và danh sách nhân sự chỉ định (/api/v2/reception/staffs).
 * Phiếu có thông tin sức khoẻ (HIV...): chỉ giữ trong bộ nhớ, không ghi localStorage, không log ra console.
 */
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { apiRequest, type ApiResult } from '../Telesales/lead/api';
import { emptyScreening, type Screening } from './logic';

const BASE = '/api/v2/declarations';

export interface Declaration {
  id: number;
  customer_id?: number;
  appointment_id?: number | null;
  visit_id?: number | null;
  answers: Partial<Screening>;
  signature?: string | null;
  flags?: string[];
  requirements?: string[];
  source?: 'kiosk' | 'staff';
  declared_at?: string | null;
  consent_at?: string | null;
  has_revisions?: boolean;
}

/** Tách chữ ký, đồng ý ra ngoài và bỏ at: phần còn lại của form là answers */
export const toAnswers = (f: Screening) => {
  const { at, signature, consent, ...answers } = f;
  return answers;
};

/** Phiếu gửi trước khi đổi quy ước lưu Nữ là 0; phiếu mới chỉ có 1 Nam, 2 Nữ */
const answerGender = (g: unknown): Screening['gender'] => (Number(g) === 1 ? 1 : g === 0 || Number(g) === 2 ? 2 : undefined);

export const toScreening = (d: Declaration): Screening => ({
  ...emptyScreening(),
  ...d.answers,
  gender: answerGender(d.answers?.gender),
  signature: d.signature ?? undefined,
  consent: true,
  at: d.declared_at ? dayjs(d.declared_at).valueOf() : 0,
});

const json = (method: 'POST' | 'PUT', body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const createDeclaration = (b: { appointment_id?: number; visit_id?: number; source: 'kiosk' | 'staff'; answers: Partial<Screening>; signature: string; consent: true }) =>
  apiRequest<{ id: number; declared_at: string }>(BASE, json('POST', b));

export const updateDeclaration = (id: number, answers: Partial<Screening>) =>
  apiRequest<Declaration>(`${BASE}/${id}`, json('PUT', { answers }));

export const getDeclaration = (id: number) => apiRequest<Declaration>(`${BASE}/${id}`);

/* Nhiều khối trên cùng màn đọc cùng một phiếu: dùng chung một lượt tải, giữ ngắn trong bộ nhớ */
const TTL = 30_000;
const cache = new Map<string, { at: number; p: Promise<ApiResult<{ items: Declaration[] }>> }>();

/** Phiếu theo lịch hẹn, hoặc theo lượt với khách vãng lai không có lịch */
export interface DeclKey { appt?: number; visit?: number }
/** Không lấy được chi tiết thì vẫn hiện phiếu, chỉ thiếu ảnh chữ ký */
const withSignatures = async (r: ApiResult<{ items: Declaration[] }>): Promise<ApiResult<{ items: Declaration[] }>> => {
  const items = await Promise.all(r.data!.items.map(async (d) => {
    if (d.signature) return d;
    const full = await getDeclaration(d.id);
    return full.ok && full.data?.signature ? { ...d, signature: full.data.signature } : d;
  }));
  return { ...r, data: { ...r.data!, items } };
};
const queryOf = ({ appt, visit }: DeclKey) => (appt != null ? `appointment_id=${appt}` : `visit_id=${visit}`);
export const loadDeclarations = (key: DeclKey) => {
  const q = queryOf(key);
  const hit = cache.get(q);
  if (hit && Date.now() - hit.at < TTL) return hit.p;
  // Danh sách không trả chữ ký (tối đa 500 KB mỗi phiếu): lấy thêm từ API chi tiết để các màn dùng chung
  const p = apiRequest<{ items: Declaration[] }>(`${BASE}?${q}`).then((r) => (r.ok && r.data ? withSignatures(r) : r));
  p.then((r) => { if (!r.ok) cache.delete(q); });
  cache.set(q, { at: Date.now(), p });
  return p;
};
export const forgetDeclarations = (key: DeclKey) => { cache.delete(queryOf(key)); };

/* ---------- Bác sĩ, tư vấn viên khách chỉ định ---------- */

export type StaffRole = 'doctor' | 'consultant';
export interface ReceptionStaff { id: number; name: string }

const staffCache: Partial<Record<StaffRole, Promise<ReceptionStaff[] | null>>> = {};
const loadStaffs = (role: StaffRole) => {
  staffCache[role] ??= apiRequest<{ items: ReceptionStaff[] }>(`/api/v2/reception/staffs?role=${role}`).then((r) => {
    if (r.ok && r.data) return r.data.items.map((x) => ({ id: x.id, name: x.name }));
    delete staffCache[role];
    return null;
  });
  return staffCache[role]!;
};

/** null = chưa tải được; undefined = đang tải */
export const useReceptionStaffs = (role: StaffRole, enabled = true) => {
  const [list, setList] = useState<ReceptionStaff[] | null>();
  useEffect(() => {
    if (!enabled) return undefined;
    let stale = false;
    loadStaffs(role).then((x) => { if (!stale) setList(x); });
    return () => { stale = true; };
  }, [role, enabled]);
  return list;
};
